import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { consumer } from 'stores/cable.js'

const USER_PREFS_KEY   = 'cal_user_prefs'
const USER_PREF_FIELDS = ['start_day_of_week', 'time_format', 'date_format', 'timezone', 'date_timezone', 'label_for_primary_timezone']

function readUserPrefs() {
  try { return JSON.parse(localStorage.getItem(USER_PREFS_KEY) || 'null') } catch { return null }
}

function saveUserPrefs(user) {
  try {
    const prefs = {}
    USER_PREF_FIELDS.forEach(k => { if (k in user) prefs[k] = user[k] })
    localStorage.setItem(USER_PREFS_KEY, JSON.stringify(prefs))
  } catch {}
}

export const useGlobalStore = defineStore('global', {
  state: () => ({
    appName: '',
    currentPath: window.location.pathname,
    signOutPath: '/users/sign_out',
    user: {
      id: null,
      display_name: '',
      email: '',
      avatar_name: '',
      avatar_url: null,
      super_admin: false,
      admin_can_go_back: false,
      // { kind, email }: how this session signed in (LoginIdentity).
      login_identity: null,
      notifications_count: 0,
      pending_verifications_count: 0,
      verified: false,
    },
    _userSubscription: null,
  }),

  getters: {
    isLoggedIn: (state) => !!state.user.id,
    hasNotifications: (state) => state.user.notifications_count > 0,

    // Returns true if the given path prefix matches the current path.
    // Used by the sidebar to highlight the active link.
    isActive: (state) => (path) => {
      if (path === '/') return state.currentPath === '/'
      return state.currentPath.startsWith(path)
    },
  },

  actions: {
    init() {
      this.appName = window.APPLICATION_NAME || ''
      this.signOutPath = window.SIGN_OUT_PATH || '/users/sign_out'
      this.currentPath = window.CURRENT_PATH || window.location.pathname

      if (window.CURRENT_USER) {
        this.user = { ...this.user, ...window.CURRENT_USER }
      }

      // Apply cached user prefs so start_day_of_week, time_format etc.
      // are available instantly before the API responds
      const cached = readUserPrefs()
      if (cached) Object.assign(this.user, cached)
    },

    setCurrentPath(path) {
      this.currentPath = path
    },

    subscribeToUserChannel() {
      if (this._userSubscription) return
      this._userSubscription = consumer.subscriptions.create('UserChannel', {
        received: (data) => {
          Object.assign(this.user, data)
          // Also patch the settings store's current_user so the settings page
          // reflects changes made in another tab without a reload.
          import('controllers/vue_components/settings/v1/store.js').then(({ useSettingsStore }) => {
            const settingsStore = useSettingsStore()
            if (settingsStore.init_data_loaded && settingsStore.current_user) {
              Object.assign(settingsStore.current_user, data)
            }
          })
        },
      })
    },

    unsubscribeFromUserChannel() {
      this._userSubscription?.unsubscribe()
      this._userSubscription = null
    },

    async fetchCurrentUser() {
      if (this.user.id) return this.user

      const { axiosRequest } = useRequest()
      const res = await axiosRequest('GET', '/api/v1/user', {}, false)
      if (!res.js_error && res.response.success) {
        this.user = { ...this.user, ...res.response.data }
      }

      return this.user
    },

    async refreshUser() {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('GET', '/api/v1/user/settings', {}, false)
      if (!res.js_error && res.response?.success) {
        Object.assign(this.user, res.response.data)
        saveUserPrefs(this.user)
      }
      return this.user
    },

    persistUserPrefs() {
      saveUserPrefs(this.user)
    },
  },
})
